import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { AccountId } from "@redotech/redo-account-id";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo, useState } from "react";

type MerchantItem = BadgeRedoListItem<AccountId>;

function toItem(merchant: {
  accountId: AccountId;
  name: string;
}): MerchantItem {
  return {
    id: merchant.accountId,
    type: "badge",
    value: merchant.accountId,
    badge: { text: merchant.name, color: "gray" },
  };
}

/**
 * Multi-select over merchants with the supplies storefront enabled. Loads the
 * full list once and filters client-side: the set is small and Redo-internal.
 */
export const SuppliesMerchantSelect = memo(function SuppliesMerchantSelect({
  accountIds,
  setAccountIds,
  disabled,
}: {
  accountIds: AccountId[];
  setAccountIds: (accountIds: AccountId[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantRpcClientContext);
  const [searchString, setSearchString] = useState("");

  const merchantsLoad = useLoad(
    async (signal) => {
      const { merchants } = await client.getSuppliesEnabledMerchants(
        {},
        { signal },
      );
      return merchants;
    },
    [client],
  );

  const allItems = useMemo(
    () => (merchantsLoad.value ?? []).map(toItem),
    [merchantsLoad.value],
  );

  const options = useMemo(() => {
    const query = searchString.trim().toLowerCase();
    return query
      ? allItems.filter((item) => {
          const text = item.badge.text;
          return typeof text === "string" && text.toLowerCase().includes(query);
        })
      : allItems;
  }, [allItems, searchString]);

  // Selected ids the list hasn't loaded yet still need a chip, or editing a
  // saved rule would silently drop merchants the picker cannot name.
  const selectedOptions = useMemo(
    () =>
      accountIds.map(
        (accountId) =>
          allItems.find((item) => item.value === accountId) ??
          toItem({ accountId, name: accountId }),
      ),
    [accountIds, allItems],
  );

  return (
    <RedoTagsDropdownInput
      description={t("Matches when the ordering merchant is any of these.")}
      disabled={disabled}
      label={t("Merchants")}
      loading={merchantsLoad.pending}
      options={options}
      placeholder={t("Select merchants")}
      searchPlaceholder={t("Search merchants...")}
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(items) =>
        setAccountIds(items.map((item) => item.value))
      }
      size="xs"
    />
  );
});
